import { z } from "zod";

export {
  DEFAULT_FREQUENCY_DAYS,
  MS_PER_DAY,
  EPOCH_ANCHOR_MS,
  type PulsePeriod,
  toISODate,
  addDaysISO,
  getPeriodForDate,
  getCheckInPeriodForDate,
  getRecentPeriods,
  isCheckInBoundary,
} from "./pulse-period";

export {
  type PulseConfig,
  type PulseConfigPending,
  PULSE_WEEKDAY_LABELS,
  isPulseConfigured,
  getFrequencyDays,
  computePeriodAnchor,
  getBootstrapPeriod,
  anchorMsFromConfig,
  getNextPeriodStart,
  schedulePulseConfigChange,
  applyPendingIfDue,
  migrateLegacyPulseConfig,
  buildInitialBusinessPulseConfig,
  buildInitialTeamPulseConfig,
  getEffectivePulseConfig,
  periodsMatch,
  getCheckInPeriodForConfig,
  isPeriodAllowedForCheckIn,
  listRecentPeriodsForConfig,
  getInFlightPeriodForConfig,
  listEndedRecurringPeriodsFromAnchor,
  type MigrateLegacyPulseOptions,
} from "./pulse-config";

import type { PulsePeriod } from "./pulse-period";

// ---------------------------------------------------------------------------
// Score enum + rubric
// ---------------------------------------------------------------------------

export const PULSE_SCORES = ["red", "yellow", "green"] as const;
export type PulseScore = (typeof PULSE_SCORES)[number];

export const PulseScoreSchema = z.enum(PULSE_SCORES);

export const PULSE_RUBRIC: Record<
  PulseScore,
  { label: string; description: string }
> = {
  green: {
    label: "Över förväntan",
    description: "Överträffar mål och förväntningar för perioden",
  },
  yellow: {
    label: "Enligt förväntan",
    description: "Uppfyller mål och presterar som förväntat",
  },
  red: {
    label: "Under förväntan",
    description: "Presterar under mål eller förväntningar för perioden",
  },
};

export const PULSE_SIGNALS = [
  "kpis",
  "wellbeing",
  "team_collab",
  "momentum",
] as const;
export type PulseSignal = (typeof PULSE_SIGNALS)[number];

export const PULSE_SIGNAL_META: Record<
  PulseSignal,
  { label: string; description: string }
> = {
  kpis: { label: "KPI:er", description: "Kvantitativa resultatmål" },
  wellbeing: { label: "Välmående", description: "Välmående och energi" },
  team_collab: {
    label: "Samarbete",
    description: "Samarbete och kommunikation",
  },
  momentum: { label: "Momentum", description: "Driv och utvecklingskurva" },
};

export const PulseSignalsMapSchema = z.record(
  z.enum(PULSE_SIGNALS),
  PulseScoreSchema,
);

// ---------------------------------------------------------------------------
// Completion status
// ---------------------------------------------------------------------------

export type PulseCompletionStatus = "not_started" | "in_progress" | "completed";

import type { PulseConfig } from "./pulse-config";
import { getCheckInPeriodForConfig } from "./pulse-config";

/**
 * Whether in-app Pulse nudges should prompt for a check-in period.
 */
export function isPulseNudgeDue(
  status: PulseCompletionStatus,
  checkInPeriod: PulsePeriod,
  today: Date = new Date(),
): boolean {
  if (status === "completed") return false;
  const periodEndMs = Date.parse(`${checkInPeriod.periodEnd}T00:00:00.000Z`);
  return today.getTime() >= periodEndMs;
}

export function isPulseNudgeDueForConfig(
  status: PulseCompletionStatus,
  config: PulseConfig,
  today: Date,
  bootstrapCompleted: boolean,
): boolean {
  const period = getCheckInPeriodForConfig(config, today, bootstrapCompleted);
  return isPulseNudgeDue(status, period, today);
}

// ---------------------------------------------------------------------------
// Firestore document shape
// ---------------------------------------------------------------------------

export interface PulseScoreRecord {
  id: string;
  businessId: string;
  periodStart: string;
  periodEnd: string;
  teamId: string | null;
  memberId: string;
  memberName: string;
  signals: Partial<Record<PulseSignal, PulseScore>>;
  note: string;
  submittedBy: string;
  submittedAt: string | null;
  updatedAt: string | null;
}

export interface PulsePeriodSummary {
  period: PulsePeriod;
  totalMembers: number;
  scoredCount: number;
  status: PulseCompletionStatus;
  distribution: Record<PulseScore, number>;
}

export interface PulseMemberTrend {
  memberId: string;
  periods: Array<{
    periodStart: string;
    periodEnd: string;
    signals: Partial<Record<PulseSignal, PulseScore>>;
    note: string;
    submittedAt: string | null;
  }>;
  currentSignals: Partial<Record<PulseSignal, PulseScore>>;
  trend: "improving" | "stable" | "declining" | "unknown";
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

export const ListPulsePeriodsInput = z.object({
  businessId: z.string().min(1),
  count: z.number().int().min(1).max(26).default(6),
  teamId: z.string().min(1).optional(),
});
export type ListPulsePeriodsInput = z.infer<typeof ListPulsePeriodsInput>;

export const ListPulseScoresInput = z.object({
  businessId: z.string().min(1),
  periodStart: z.string().min(1),
  periodEnd: z.string().min(1),
  teamId: z.string().min(1).optional(),
});
export type ListPulseScoresInput = z.infer<typeof ListPulseScoresInput>;

export const UpsertPulseScoreInput = z.object({
  businessId: z.string().min(1),
  periodStart: z.string().min(1),
  periodEnd: z.string().min(1),
  memberId: z.string().min(1),
  teamId: z.string().min(1).nullish(),
  signals: PulseSignalsMapSchema,
  note: z.string().max(500).default(""),
});
export type UpsertPulseScoreInput = z.infer<typeof UpsertPulseScoreInput>;

export const BulkUpsertPulseScoresInput = z.object({
  businessId: z.string().min(1),
  periodStart: z.string().min(1),
  periodEnd: z.string().min(1),
  scores: z.array(
    z.object({
      memberId: z.string().min(1),
      teamId: z.string().min(1).nullish(),
      signals: PulseSignalsMapSchema,
      note: z.string().max(500).default(""),
    }),
  ),
});
export type BulkUpsertPulseScoresInput = z.infer<
  typeof BulkUpsertPulseScoresInput
>;

export const GetPulseCompletionInput = z.object({
  businessId: z.string().min(1),
  periodStart: z.string().min(1).optional(),
  periodEnd: z.string().min(1).optional(),
  teamId: z.string().min(1).optional(),
});
export type GetPulseCompletionInput = z.infer<typeof GetPulseCompletionInput>;

export const GetMemberPulseHistoryInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  limit: z.number().int().min(1).max(52).default(12),
  teamId: z.string().min(1).optional(),
});
export type GetMemberPulseHistoryInput = z.infer<
  typeof GetMemberPulseHistoryInput
>;

export const GetTeamPulseSummaryInput = z.object({
  businessId: z.string().min(1),
  teamId: z.string().min(1).optional(),
  periodStart: z.string().min(1).optional(),
  periodEnd: z.string().min(1).optional(),
});
export type GetTeamPulseSummaryInput = z.infer<typeof GetTeamPulseSummaryInput>;

export const GetPulseConfigInput = z.object({
  businessId: z.string().min(1),
  teamId: z.string().min(1).optional(),
});
export type GetPulseConfigInput = z.infer<typeof GetPulseConfigInput>;

export const ConfigurePulseInput = z.object({
  businessId: z.string().min(1),
  checkInFrequencyDays: z.number().int().min(1).max(90).default(14),
  periodStartWeekday: z.number().int().min(0).max(6).default(1),
});
export type ConfigurePulseInput = z.infer<typeof ConfigurePulseInput>;

export const SchedulePulseConfigChangeInput = z.object({
  businessId: z.string().min(1),
  teamId: z.string().min(1).optional(),
  checkInFrequencyDays: z.number().int().min(1).max(90),
  periodStartWeekday: z.number().int().min(0).max(6),
});
export type SchedulePulseConfigChangeInput = z.infer<
  typeof SchedulePulseConfigChangeInput
>;

export const CancelPulseConfigChangeInput = z.object({
  businessId: z.string().min(1),
  teamId: z.string().min(1).optional(),
});
export type CancelPulseConfigChangeInput = z.infer<
  typeof CancelPulseConfigChangeInput
>;

export const SetTeamPulseOverrideInput = z.object({
  businessId: z.string().min(1),
  teamId: z.string().min(1),
  checkInFrequencyDays: z.number().int().min(1).max(90),
  periodStartWeekday: z.number().int().min(0).max(6),
});
export type SetTeamPulseOverrideInput = z.infer<
  typeof SetTeamPulseOverrideInput
>;

export const ClearTeamPulseOverrideInput = z.object({
  businessId: z.string().min(1),
  teamId: z.string().min(1),
});
export type ClearTeamPulseOverrideInput = z.infer<
  typeof ClearTeamPulseOverrideInput
>;

/** @deprecated Use scheduleConfigChange */
export const UpdatePulseConfigInput = z.object({
  businessId: z.string().min(1),
  checkInFrequencyDays: z.number().int().min(1).max(90),
});
export type UpdatePulseConfigInput = z.infer<typeof UpdatePulseConfigInput>;

export const PulseConfigSchema = z.object({
  checkInFrequencyDays: z.number().int().min(1).max(90),
  configuredAt: z.string(),
  periodStartWeekday: z.number().int().min(0).max(6),
  periodAnchor: z.string(),
  bootstrapPeriod: z
    .object({
      periodStart: z.string(),
      periodEnd: z.string(),
    })
    .optional(),
  pending: z
    .object({
      checkInFrequencyDays: z.number().int().min(1).max(90),
      periodStartWeekday: z.number().int().min(0).max(6),
      effectiveFrom: z.string(),
      requestedAt: z.string(),
      requestedBy: z.string(),
    })
    .optional(),
});

export type PulseConfigResponse = {
  isConfigured: boolean;
  businessConfigured: boolean;
  usesTeamOverride: boolean;
  config: import("./pulse-config").PulseConfig | null;
  pending: import("./pulse-config").PulseConfigPending | null;
  bootstrapCompleted: boolean;
};
